import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
  inject,
} from '@angular/core';
import { Icon, UploadComponent, UploadEvent } from '@web/shared/ui';
import { combineLatest, Observable, tap } from 'rxjs';
import { blank, Guid, Nillable, valueOrDefault } from '@web/utils';
import { PublicMultimediaDataSourceService } from '../../services/public-multimedia-data-source.service';
import { MultimediaType } from '../../../shared/enums/multimedia-type';
import { MultimediaThumbnailVariant } from '../../../shared/enums/multimedia-thumbnail-variant';
import { MultimediaGridLayoutComponent } from '../../../admin/components/multimedia-grid-layout/multimedia-grid-layout.component';
import { MultimediaGridLayoutItemComponent } from '../../../admin/components/multimedia-grid-layout-item/multimedia-grid-layout-item.component';
import { MultimediaTileComponent } from '../../../shared/components/multimedia-tile/multimedia-tile.component';
import { PublicShopFileThumbnailUrlPipe } from '../../pipes/public-shop-file-thumbnail-url.pipe';
import { MultimediaTileThumbnailComponent } from '../../../shared/components/multimedia-tile-thumbnail/multimedia-tile-thumbnail.component';

@Component({
  selector: 'app-public-shop-multimedia-upload',
  imports: [
    UploadComponent,
    MultimediaGridLayoutComponent,
    MultimediaGridLayoutItemComponent,
    MultimediaTileComponent,
    PublicShopFileThumbnailUrlPipe,
    MultimediaTileThumbnailComponent,
  ],
  templateUrl: './public-shop-multimedia-upload.component.html',
  styleUrl: './public-shop-multimedia-upload.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicShopMultimediaUploadComponent {
  /* prettier-ignore */
  private readonly multimediaDataSource = inject(PublicMultimediaDataSourceService);

  public readonly disabled = input(false);
  public readonly allowedMimeTypes = input<string>('');
  public readonly maxFileSize = input<number>(2 * 1000 * 1000);
  public readonly allowedMultimediaTypes = input<Nillable<MultimediaType[]>>();
  public readonly maxNumberOfFiles = input<number>(5);
  public readonly multi = input<boolean>(false);
  public readonly selectFilesEvent = output<Guid[]>();

  protected readonly chosenId = signal<Nillable<Guid>>(null);
  protected readonly Icon = Icon;
  protected readonly Large = MultimediaThumbnailVariant.Large;
  protected readonly uploadQueue = signal<File[]>([]);
  protected readonly uploaded = signal<Guid[]>([]);

  public constructor() {
    this.listenToSelectFilesEvent();
  }

  public handleRemoveClickEvent(id: Guid): void {
    this.uploaded.update((ids) =>
      valueOrDefault(ids, []).filter((currentId) => currentId !== id),
    );
  }

  protected readonly uploadFn = (event: UploadEvent) => this.upload(event);

  private upload(event: UploadEvent): Observable<void> {
    const { files } = event;

    this.addToQueue(files);

    return combineLatest(
      files.map((file) =>
        this.multimediaDataSource.shopUpload(file).pipe(
          tap({
            next: (id) => {
              this.removeFromQueue(file);
              this.addToUploaded(id);
            },
            error: () => {
              this.removeFromQueue(file);
            },
          }),
        ),
      ),
    ).pipe(blank());
  }

  private addToUploaded(id: Guid): void {
    this.uploaded.update((ids) => [id, ...ids]);
  }

  private addToQueue(filesToAdd: File[]): void {
    this.uploadQueue.update((files) => [...filesToAdd, ...files]);
  }

  private removeFromQueue(fileToRemove: File): void {
    this.uploadQueue.update((result) =>
      result.filter((file) => file !== fileToRemove),
    );
  }
  private listenToSelectFilesEvent(): void {
    effect(() => this.notifySelectFilesEvent());
  }

  private notifySelectFilesEvent(): void {
    this.selectFilesEvent.emit(this.uploaded());
  }
}
